import { useNavigate, useSearchParams } from 'react-router-dom'
import { ROUTE_CLUBS, ROUTE_CLUB_MANAGEMENT } from '@/constants/Routes'
import { useCanUseCoaching } from '@/hooks/useCanUseCoaching'
import { useCanUseBenefits } from '@/hooks/useCanUseBenefits'
import { ClubManagementData } from '../hooks/useClubManagementData'
import { ClubOverviewTab } from './ClubOverviewTab'
import { ClubMembersTab } from './ClubMembersTab'
import { ClubMatchesTab } from './ClubMatchesTab'
import { ClubBenefitsTab } from './ClubBenefitsTab'
import { ClubCoachingTab } from './ClubCoachingTab'
import { ClubSettingsTab } from './ClubSettingsTab'
import { MemberDetailModal } from './MemberDetailModal'
import { TabBar, RoleBadge } from './ui'

interface ClubDetailViewProps {
  club: ClubManagementData
  onBack?: () => void
  className?: string
}

type TabId = 'overview' | 'members' | 'matches' | 'coaching' | 'benefits' | 'settings'

const TAB_IDS: TabId[] = ['overview', 'members', 'matches', 'coaching', 'benefits', 'settings']

export const ClubDetailView = ({ club, onBack, className = '' }: ClubDetailViewProps) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const isDirector = club.role.role === 'DIRECTOR' || club.role.role === 'ORGANIZER'
  const isActualDirector = club.role.role === 'DIRECTOR'
  // Coaching is gated by the same feature flag as the per-member coaching tools; the tab itself
  // generalizes over whatever member-acting capabilities the club has active.
  const canCoach = useCanUseCoaching()
  // The club's end of the member claim flow. Same flag as the member's end, because the two are the
  // same feature seen from both sides — see useCanUseBenefits.
  const canUseBenefits = useCanUseBenefits()

  // Active tab + open member both live in the URL so a view/member is shareable and the browser
  // back button works (e.g. `?club=12&tab=members&member=345`).
  const tabParam = searchParams.get('tab') as TabId | null
  const activeTab: TabId = tabParam && TAB_IDS.includes(tabParam) ? tabParam : 'overview'

  const setActiveTab = (id: TabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', id)
      return next
    })
  }

  const memberParam = searchParams.get('member')
  const openMemberId = memberParam && Number.isFinite(Number(memberParam)) ? Number(memberParam) : null
  const memberTab = searchParams.get('memberTab') === 'coaching' ? 'coaching' : 'matches'
  const closeMember = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('member')
      next.delete('memberTab')
      return next
    })
  }

  const handleClubClick = () => {
    navigate(`${ROUTE_CLUBS}/${club.clubId}`, {
      state: { from: ROUTE_CLUB_MANAGEMENT },
    })
  }

  const tabs: { id: TabId; label: string; visible: boolean }[] = [
    { id: 'overview', label: 'Overview', visible: true },
    { id: 'members', label: 'Members', visible: true },
    { id: 'matches', label: 'Matches', visible: true },
    { id: 'coaching', label: 'Coaching', visible: canCoach },
    // Visible to anyone who can manage the club: working the claim list is front-desk work, and
    // gating it on the (separate) permission to change what the club offers would hide the list
    // from exactly the people who need it.
    //
    // KNOWN GAP — this is role inference, not a permission check, and it locks out the delegate the
    // RBAC role exists for. `benefits.offers:edit` / `benefits.rules:edit` come with
    // `club-benefits-manager` (core/.../rbac/RbacCatalog.kt), whose whole purpose is letting a
    // director hand benefits to staff who are not directors. Such a person fails `isDirector`.
    //
    // It cannot be fixed here, and the fix is not a different client-side guess:
    //   * `club.role.role` comes from `GET /club/roles/v1.0/all`, which reads `club_members` and
    //     returns only the legacy flat enum — no RBAC role or permission URNs. A benefits manager
    //     with no legacy row does not appear in that response at all, so `useClubManagementData`
    //     filters them out before this component ever renders. The lockout starts upstream.
    //   * No endpoint a non-admin can call answers "which permissions do I hold for this club".
    //     The only self-scoped signal in the whole benefits API is `canEdit` on
    //     `/club/{clubId}/benefits/v1.0/eligibility/templates` — and it is about `benefits.rules:edit`
    //     specifically, ANDed with the club whitelist and the targeting flag, so it cannot stand in
    //     for "may see the claim list" (and reading it needs the club to be reachable already).
    //
    // Unblocking this needs the server to say so: either RBAC roles on the club-roles listing, or a
    // self-scoped permissions endpoint for a club.
    { id: 'benefits', label: 'Benefits', visible: isDirector && canUseBenefits },
    { id: 'settings', label: 'Settings', visible: isDirector },
  ]

  return (
    <div className={className}>
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to clubs
        </button>
      )}

      {/* Compact Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-gray-900 font-bold text-base truncate">{club.clubName}</h3>
            <RoleBadge role={club.role.role} />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-gray-500 text-xs">{club.shortAddress}</span>
            <span className="text-gray-300">·</span>
            <span className="text-gray-400 text-xs">ID: {club.clubId}</span>
            <span className="text-gray-300">·</span>
            <button onClick={handleClubClick} className="text-primary-600 hover:text-primary-500 text-xs">
              view public page
            </button>
          </div>
        </div>
      </div>

      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id as TabId)} className="mb-3" />

      {activeTab === 'overview' && <ClubOverviewTab club={club} isDirector={isActualDirector} />}
      {activeTab === 'members' && <ClubMembersTab clubId={club.clubId} isDirector={isDirector} />}
      {activeTab === 'matches' && <ClubMatchesTab clubId={club.clubId} isDirector={isDirector} />}
      {activeTab === 'coaching' && canCoach && <ClubCoachingTab clubId={club.clubId} />}
      {activeTab === 'benefits' && isDirector && canUseBenefits && <ClubBenefitsTab clubId={club.clubId} />}
      {activeTab === 'settings' && isDirector && <ClubSettingsTab clubId={club.clubId} />}

      {openMemberId != null && (
        <MemberDetailModal
          key={`${openMemberId}:${memberTab}`}
          clubId={club.clubId}
          userId={openMemberId}
          onClose={closeMember}
          initialTab={memberTab}
        />
      )}
    </div>
  )
}
